/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @interface FiltersAPI
 * @classdesc API class that is used to control filters.
 * @example globalFilters.getFilters()
 */
define([], () => {
	class FiltersAPI {

		/**
		 * @function FiltersAPI#getFiltersDesc
		 * @description Gets the list of filter UI descriptions.
		 * @private
		 * @returns {Object[]} Array of filter descriptions
		 *
		 * @todo - document the return syntax
		 */
		getFiltersDesc() { }

		/**
		 * @function FiltersAPI#getFilterList
		 * @description Gets the list of filter specifications.
		 * @returns {Object[]} Array of filter specifications
		 */
		getFilterList() {}

		/**
		 * @function FiltersAPI#filterExists
		 * @description Checks whether a filter for a data item exists or not.
		 * @private
		 *
		 * @param {Object} itemContext Object {@link ItemContext} that describes filter data items context.
		 * @param {string} itemContext.itemId Data item identifier
		 * @param {string[]} [itemContext.itemIds] Data item identifiers for data point filters
		 * @param {string} [item.aggregationType] Data item aggregation type
		 * @returns {boolean} True if a filter for the data item exists, otherwise false.
		 */
		filterExists(/* itemContext */) { }

		/**
		 * @function FiltersAPI#addFilter
		 * @description Adds a new filter to the widget.
		 * @public
		 * @async
		 *
		 * @param {Object} itemContext Object {@link ItemContext} that describes filter data items context.
		 * @param {string} itemContext.itemId Data item identifier
		 * @param {string[]} [itemContext.itemIds] Data item identifiers for data point filters
		 * @param {string} [itemContext.aggregationType] Data item aggregation type
		 * @param {Object} [filterContext] Filter context object
		 * @param {Object[]} [filterContext.values] Array of values applied to the filter
		 * @param {Object} [filterContext.from] Start range value
		 * @param {Object} [filterContext.to] End range value
		 * @param {boolean} [filterContext.exclude] Use filter values as exclusions
		 * @param {Object} [filterContext.conditionsInfo] Filter conditions information
		 * @param {Object} [filterContext.conditionsInfo.conditions] Filter conditions
		 * @param {Object} [filterContext.conditionsInfo.conditions.operator = 'containsignorecase'] Filter condition operator
		 * @param {Object} [filterContext.conditionsInfo.conditions.values] Filter condition values
		 * @param {string[]} [filterContext.conditionsInfo.excludedValues = []]
		 * @param {Object} setOptions Options for visTransaction
		 * @returns {Object} an object that includes the id of the added filter
		 * @example
		 *	filters.addFilter({
		 *     itemId: 'sales_and_marketing_csv.Product_line'
		 *  }, {
		 *     values: [{
		 *        u: 'sales_and_marketing_csv.Year_->[2006]',
		 *        d: '2006'
		 *     }]
		 * });
		 *
		 *  filters.addFilter({
		 *     itemId: 'sales_and_marketing_csv.Quantity'
		 *  }, {
		 *     from: {
		 *        u: 0,
		 *        d: 0
		 *     },
		 *     to: {
		 *        u: 500000,
		 *        d: 500000
		 *     }
		 * });
		 *
		 *
		 */
		addFilter(/* itemContext, filterContext, setOptions */) { }

		/**
		 * @function FiltersAPI#editFilter
		 * @description Edits an existing filter.
		 * @public
		 * @async
		 *
		 * @param {Object} itemContext Object {@link ItemContext} that describes filter data items context.
		 * @param {string} itemContext.itemId Data item identifier
		 * @param {string[]} [itemContext.itemIds] Data item identifiers for data point filters
		 * @param {string} [itemContext.aggregationType] Data item aggregation type
		 * @param {Object} [filterContext] Filter context object
		 * @param {Object[]} [filterContext.values] Array of values applied to the filter
		 * @param {Object} [filterContext.from] Start range value
		 * @param {Object} [filterContext.to] End range value
		 * @param {boolean} [filterContext.exclude] Use filter values as exclusions
		 * @param {Object} setOptions Options for visTransaction
		 * @example
		 * filters.editFilter({
		 *     itemId: 'sales_and_marketing_csv.Product_line'
		 *   }, {
		 *     values: [{
		 *         u: 'sales_and_marketing_csv.Year_->[2006]',
		 *         d: '2006'
		 *     }]
		 * });
		 */
		editFilter(/* itemContext, filterContext, setOptions */) { }

		/**
		 * @function FiltersAPI#removeFilter
		 * @description Removes an existing filter
		 * @public
		 * @async
		 *
		 * @param {Object} itemContext Object {@link ItemContext} that describes filter data items context.
		 * @param {string} itemContext.itemId Data item identifier
		 * @param {string[]} [itemContext.itemIds] Data item identifiers for data point filters
		 * @param {string} [item.aggregationType] Data item aggregation type
		 * @param {Object} setOptions Options for visTransaction
		 * @example
		 *	filters.removeFilter({
		 *		itemId: 'sales_and_marketing_csv.Product_line'
			*	});
			*/
		removeFilter(/* itemContext, setOptions */) { }
	}
	return FiltersAPI;
});
